import { useCallback, useEffect, useRef, useState } from 'react';

import BackendService from '../services/backendService/backendService';
import {
  buildWordTimings,
  ElevenLabsAlignment,
  extractPlainTextWithOffsets,
  WordTiming,
} from '../utils/ttsAlignment';

export type TTSState = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

/** Browser-side cache: keyed by `text|voiceId`, stores the API response to avoid re-generating. */
interface CachedTTSResponse {
  audioUrl: string;
  alignment: ElevenLabsAlignment;
}
const ttsCache = new Map<string, CachedTTSResponse>();

export interface UseTextToSpeechReturn {
  state: TTSState;
  currentWordIndex: number;
  wordTimings: WordTiming[];
  analyserNode: AnalyserNode | null;
  volume: number;
  playbackRate: number;
  generate: (voiceId: string) => Promise<void>;
  play: () => void;
  pause: () => void;
  stop: () => void;
  seek: (delta: number) => void;
  setVolume: (volume: number) => void;
  setPlaybackRate: (rate: number) => void;
  error: string | null;
}

export function useTextToSpeech(slateContent: string | null): UseTextToSpeechReturn {
  const [state, setState] = useState<TTSState>('idle');
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [wordTimings, setWordTimings] = useState<WordTiming[]>([]);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(0.6);
  const [playbackRate, setPlaybackRateState] = useState(1);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const wordTimingsRef = useRef<WordTiming[]>([]);
  const volumeRef = useRef(0.6);
  const playbackRateRef = useRef(1);

  // Keep ref in sync with state for use in rAF loop
  useEffect(() => {
    wordTimingsRef.current = wordTimings;
  }, [wordTimings]);

  const cleanup = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioRef.current) {
      const audio = audioRef.current;
      audioRef.current = null;
      audio.pause();
      audio.onended = null;
      audio.removeAttribute('src');
      audio.load();
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      const ctx = audioContextRef.current;
      audioContextRef.current = null;
      ctx.close().catch(() => {
        /* teardown */
      });
    }
    setAnalyserNode(null);
    setCurrentWordIndex(-1);
  }, []);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);

  const startWordTracking = useCallback(() => {
    const tick = () => {
      const audio = audioRef.current;
      const timings = wordTimingsRef.current;
      if (!audio || timings.length === 0) return;

      const currentTime = audio.currentTime;

      // Find the word that contains the current time
      let idx = -1;
      for (let i = 0; i < timings.length; i++) {
        if (currentTime >= timings[i].startTime && currentTime < timings[i].endTime) {
          idx = i;
          break;
        }
        // If we're between words, highlight the next word
        if (
          i < timings.length - 1 &&
          currentTime >= timings[i].endTime &&
          currentTime < timings[i + 1].startTime
        ) {
          idx = i;
          break;
        }
      }

      // If past the last word's start, highlight the last word
      if (
        idx === -1 &&
        timings.length > 0 &&
        currentTime >= timings[timings.length - 1].startTime
      ) {
        idx = timings.length - 1;
      }

      setCurrentWordIndex(prev => (prev !== idx ? idx : prev));

      if (!audio.paused && !audio.ended) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const generate = useCallback(
    async (voiceId: string) => {
      if (!slateContent) return;

      cleanup();
      setState('loading');
      setError(null);

      try {
        const { plainText, wordMap } = extractPlainTextWithOffsets(slateContent);

        if (!plainText.trim()) {
          setState('error');
          setError('No text content to read aloud');
          return;
        }

        // Check browser-side cache first
        const cacheKey = `${plainText}|${voiceId}`;
        let data: CachedTTSResponse;

        const cached = ttsCache.get(cacheKey);
        if (cached) {
          data = cached;
        } else {
          data = await BackendService.instance.generateTTS(plainText, voiceId);
          ttsCache.set(cacheKey, data);
        }

        // Build word timings from alignment
        const timings = buildWordTimings(data.alignment, wordMap);
        setWordTimings(timings);
        wordTimingsRef.current = timings;

        // Play audio from signed URL
        const audio = new Audio(data.audioUrl);
        audio.crossOrigin = 'anonymous';
        audio.volume = volumeRef.current;
        audio.playbackRate = playbackRateRef.current;
        audioRef.current = audio;

        // Create AudioContext for analyser (must be in user gesture context)
        const ctx = new AudioContext();
        audioContextRef.current = ctx;

        const source = ctx.createMediaElementSource(audio);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;

        source.connect(analyser);
        analyser.connect(ctx.destination);
        setAnalyserNode(analyser);

        audio.onended = () => {
          setState('idle');
          setCurrentWordIndex(-1);
          if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
          }
        };

        await audio.play();
        setState('playing');
        startWordTracking();
      } catch (err) {
        setState('error');
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    },
    [slateContent, cleanup, startWordTracking],
  );

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }

    audio.play();
    setState('playing');
    startWordTracking();
  }, [startWordTracking]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setState('paused');
  }, []);

  const stop = useCallback(() => {
    cleanup();
    setWordTimings([]);
    setState('idle');
  }, [cleanup]);

  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    volumeRef.current = clamped;
    if (audioRef.current) {
      audioRef.current.volume = clamped;
    }
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    const clamped = Math.round(Math.max(0.5, Math.min(2, rate)) * 10) / 10;
    setPlaybackRateState(clamped);
    playbackRateRef.current = clamped;
    if (audioRef.current) {
      audioRef.current.playbackRate = clamped;
    }
  }, []);

  const seek = useCallback(
    (delta: number) => {
      const audio = audioRef.current;
      if (!audio || !isFinite(audio.duration)) return;
      audio.currentTime = Math.max(
        0,
        Math.min(audio.duration, audio.currentTime + delta),
      );
      if (audio.paused) {
        startWordTracking();
      }
    },
    [startWordTracking],
  );

  return {
    state,
    currentWordIndex,
    wordTimings,
    analyserNode,
    volume,
    playbackRate,
    generate,
    play,
    pause,
    stop,
    seek,
    setVolume,
    setPlaybackRate,
    error,
  };
}
